import { IndustryName } from '../../../data/industryDefaults';

export interface FieldHelpContent {
  title: string;
  icon?: string;
  description: string;
  details?: string[];
  benchmarkTable?: {
    title: string;
    headers: string[];
    rows: (string | number)[][];
  };
  calculation?: {
    formula: string;
    example: string;
    factors?: string[];
  };
  tips?: string[];
}

// Industry-specific benchmark data for vacancy cost
const getVacancyCostBenchmarks = (industryName: IndustryName): (string | number)[][] => {
  const industryBenchmarks: Record<string, (string | number)[][]> = {
    'Technology': [
      ['Entry Level', '$250-350'],
      ['Mid-Level', '$400-500'],
      ['Senior/Specialized', '$600-800'],
      ['Executive', '$1000+']
    ],
    'Healthcare Providers & Services': [
      ['Support Staff', '$200-300'],
      ['Nurses/Technicians', '$350-450'],
      ['Specialized Clinical', '$500-700'],
      ['Physicians/Leadership', '$800+']
    ],
    'Financial Services': [
      ['Administrative', '$200-300'],
      ['Analysts', '$350-450'],
      ['Senior Analysts/Managers', '$500-700'],
      ['Directors/VPs', '$900+']
    ],
    default: [
      ['Entry Level', '$200-300'],
      ['Mid-Level', '$350-450'],
      ['Senior Level', '$500-650'],
      ['Executive', '$800+']
    ]
  };

  return industryBenchmarks[industryName] || industryBenchmarks.default;
};

// Industry-specific scheduling time benchmarks
const getSchedulingTimeBenchmarks = (industryName: IndustryName): (string | number)[][] => {
  const industryBenchmarks: Record<string, (string | number)[][]> = {
    'Technology': [
      ['Manual (email/phone)', '72-96 hours', 'High cost'],
      ['Basic Calendar Tools', '48-72 hours', 'Medium cost'],
      ['Scheduling Software', '24-48 hours', 'Low cost'],
      ['AI-Automated', '4-12 hours', 'Minimal']
    ],
    'Healthcare Providers & Services': [
      ['Manual Coordination', '96-120 hours', 'Very high'],
      ['Shared Calendars', '72-96 hours', 'High cost'],
      ['Scheduling Systems', '48-72 hours', 'Medium cost'],
      ['Integrated Platforms', '24-48 hours', 'Low cost']
    ],
    default: [
      ['Manual Process', '72-96 hours', 'High cost'],
      ['Semi-Automated', '48-72 hours', 'Medium cost'],
      ['Automated Tools', '24-48 hours', 'Low cost'],
      ['Fully Integrated', '12-24 hours', 'Minimal']
    ]
  };

  return industryBenchmarks[industryName] || industryBenchmarks.default;
};

// Industry-specific salary benchmarks
const getAvgSalaryBenchmarks = (industryName: IndustryName): (string | number)[][] => {
  const industryBenchmarks: Record<string, (string | number)[][]> = {
    'Technology': [
      ['Entry Level Developer', '$70,000-90,000'],
      ['Mid-Level Engineer', '$100,000-130,000'],
      ['Senior Engineer', '$140,000-180,000'],
      ['Tech Lead/Architect', '$170,000-220,000'],
      ['Engineering Manager', '$180,000-250,000']
    ],
    'Healthcare Providers & Services': [
      ['Medical Assistant', '$35,000-45,000'],
      ['Registered Nurse', '$70,000-90,000'],
      ['Nurse Practitioner', '$110,000-130,000'],
      ['Physician', '$200,000-350,000'],
      ['Department Head', '$150,000-250,000']
    ],
    'Financial Services': [
      ['Junior Analyst', '$60,000-80,000'],
      ['Financial Analyst', '$75,000-100,000'],
      ['Senior Analyst', '$100,000-130,000'],
      ['Portfolio Manager', '$120,000-180,000'],
      ['Director/VP', '$150,000-300,000']
    ],
    'Retail & E-commerce': [
      ['Sales Associate', '$28,000-35,000'],
      ['Department Manager', '$45,000-60,000'],
      ['Store Manager', '$60,000-85,000'],
      ['Regional Manager', '$90,000-120,000'],
      ['Director', '$120,000-160,000']
    ],
    'Manufacturing & Industrial': [
      ['Production Worker', '$35,000-45,000'],
      ['Team Lead', '$50,000-65,000'],
      ['Production Manager', '$70,000-90,000'],
      ['Plant Manager', '$95,000-130,000'],
      ['Operations Director', '$130,000-180,000']
    ],
    default: [
      ['Entry Level', '$40,000-60,000'],
      ['Mid-Level', '$60,000-90,000'],
      ['Senior Level', '$90,000-130,000'],
      ['Management', '$110,000-160,000'],
      ['Executive', '$150,000-250,000']
    ]
  };

  return industryBenchmarks[industryName] || industryBenchmarks.default;
};

export const FIELD_HELP_CONTENT: Record<string, (industryName?: IndustryName) => FieldHelpContent> = {
  avgSalary: (industryName = 'Technology') => ({
    title: 'Average Salary Explained',
    icon: '💼',
    description: 'The typical annual salary for positions you hire. This should be a weighted average if you hire multiple role types, reflecting your actual hiring mix.',
    details: [
      'Include base salary only (not benefits or bonuses)',
      'Consider your typical hiring distribution across levels',
      'Factor in geographic location adjustments',
      'Use market-rate data for accuracy',
      'Update regularly to reflect current market conditions'
    ],
    benchmarkTable: {
      title: 'Industry Salary Benchmarks',
      headers: ['Role Type', 'Typical Range'],
      rows: getAvgSalaryBenchmarks(industryName)
    },
    calculation: {
      formula: 'Weighted Average = Σ(Role Salary × Hiring %) for all roles',
      example: 'If you hire 40% junior ($70k), 40% mid ($110k), 20% senior ($160k): (70k×0.4) + (110k×0.4) + (160k×0.2) = $104,000',
      factors: [
        'Your actual hiring mix percentages',
        'Local market conditions',
        'Industry competition for talent',
        'Required skill sets and experience'
      ]
    },
    tips: [
      'Use the "Calculate Values" button to automatically compute based on your hiring profile',
      'Consider total compensation but enter base salary only',
      'Review quarterly to ensure market alignment',
      'Factor in both current employees and typical new hire salaries'
    ]
  }),

  dailyVacancyCost: (industryName = 'Technology') => ({
    title: 'Daily Vacancy Cost Explained',
    icon: '💰',
    description: 'The total daily cost of an unfilled position, including lost productivity, revenue impact, overtime costs for coverage, and effects on team morale and performance.',
    details: [
      'Lost productivity and revenue from the vacant role',
      'Overtime costs for existing staff covering the work',
      'Impact on team morale and increased turnover risk',
      'Delayed projects and missed opportunities',
      'Potential customer satisfaction impact'
    ],
    benchmarkTable: {
      title: 'Industry Benchmarks',
      headers: ['Role Level', 'Daily Cost Range'],
      rows: getVacancyCostBenchmarks(industryName)
    },
    calculation: {
      formula: '(Annual Salary × Multiplier) ÷ 365 days',
      example: 'For a $90,000 role with 2.5x multiplier: ($90,000 × 2.5) ÷ 365 = $616/day',
      factors: [
        '1.5-2x for support/administrative roles',
        '2-3x for core business roles',
        '3-4x for revenue-generating or critical technical roles',
        '4x+ for executive or highly specialized positions'
      ]
    },
    tips: [
      'Consider both direct costs (salary, benefits) and indirect costs (lost productivity, opportunity cost)',
      'Revenue-generating roles typically have higher multipliers due to direct revenue impact',
      'Factor in your industry\'s competitive landscape and talent scarcity'
    ]
  }),

  avgConfirmationTime: (industryName = 'Technology') => ({
    title: 'Scheduling Time (Time to Confirm)',
    icon: '⏱️',
    description: 'The average time from when an interview is requested to when it\'s confirmed with all participants. This metric directly impacts candidate experience and time-to-fill.',
    details: [
      'Includes all back-and-forth communication',
      'Time spent finding mutually available slots',
      'Coordination across multiple calendars',
      'Rescheduling attempts and conflicts'
    ],
    benchmarkTable: {
      title: 'Scheduling Method Comparison',
      headers: ['Method', 'Typical Time', 'Impact'],
      rows: getSchedulingTimeBenchmarks(industryName)
    },
    tips: [
      'Faster confirmation times lead to better candidate experience',
      'Top candidates often accept offers elsewhere during scheduling delays',
      'Automation can reduce scheduling time by 70-90%',
      `In ${industryName}, fast scheduling is crucial due to competitive talent market`
    ]
  }),

  adminTimePerHire: (industryName = 'Technology') => ({
    title: 'Scheduling Effort per Interview',
    icon: '📋',
    description: 'Total time your team spends on scheduling activities per interview, including emails, phone calls, calendar management, and rescheduling.',
    details: [
      'Initial outreach and availability collection',
      'Calendar coordination and room booking',
      'Sending confirmations and reminders',
      'Handling rescheduling requests',
      'Managing no-shows and follow-ups'
    ],
    benchmarkTable: {
      title: 'Time Breakdown by Activity',
      headers: ['Activity', 'Manual (min)', 'Automated (min)'],
      rows: [
        ['Collecting availability', '30-45', '0-5'],
        ['Finding time slots', '20-30', '0-2'],
        ['Sending invites/confirms', '15-20', '0-1'],
        ['Rescheduling (when needed)', '30-40', '5-10'],
        ['Total per interview', '95-135', '5-18']
      ]
    },
    calculation: {
      formula: 'Sum of all scheduling activities × Number of interviews per hire',
      example: 'If 120 min per interview × 5 interviews per hire = 600 minutes (10 hours) per hire',
    },
    tips: [
      'Track actual time spent, not estimates',
      'Include all team members involved (recruiters, coordinators, hiring managers)',
      'Consider the opportunity cost of this admin time',
      'Automation can reduce admin time by 85-95%'
    ]
  }),

  reschedulingRate: (industryName = 'Technology') => ({
    title: 'Interview Rescheduling Rate',
    icon: '🔄',
    description: 'Percentage of scheduled interviews that need to be rescheduled. High rates indicate scheduling friction and impact candidate experience.',
    details: [
      'Last-minute conflicts from interviewers',
      'Candidate availability changes',
      'Room/resource conflicts',
      'Technical issues for virtual interviews'
    ],
    benchmarkTable: {
      title: 'Rescheduling Impact Analysis',
      headers: ['Rate Range', 'Impact Level', 'Common Causes'],
      rows: [
        ['< 10%', 'Excellent', 'Well-coordinated process'],
        ['10-20%', 'Good', 'Normal business conflicts'],
        ['20-30%', 'Average', 'Poor initial coordination'],
        ['> 30%', 'Poor', 'Systematic scheduling issues']
      ]
    },
    tips: [
      'Each reschedule adds 30-60 minutes of admin work',
      'Candidates who experience rescheduling are 40% less likely to accept offers',
      'Focus on better upfront availability collection',
      'Consider buffer time in interviewer calendars'
    ]
  }),

  interviewNoShowRate: (industryName = 'Technology') => ({
    title: 'Interview No-Show Rate',
    icon: '❌',
    description: 'Percentage of scheduled interviews where the candidate doesn\'t attend without prior notice. Indicates issues with engagement, communication, or scheduling process.',
    details: [
      'Candidates accepting other offers',
      'Poor communication or unclear instructions',
      'Too much time between scheduling and interview',
      'Lack of reminder system'
    ],
    benchmarkTable: {
      title: 'No-Show Rate Benchmarks',
      headers: ['Rate', 'Assessment', 'Typical Causes'],
      rows: [
        ['< 5%', 'Excellent', 'Strong engagement process'],
        ['5-10%', 'Good', 'Industry average'],
        ['10-15%', 'Concerning', 'Communication gaps'],
        ['> 15%', 'Critical', 'Process breakdown']
      ]
    },
    tips: [
      'Send automated reminders 24 hours and 1 hour before interviews',
      'Reduce time between scheduling and interview date',
      'Provide clear instructions and contact information',
      'Follow up on no-shows to understand reasons'
    ]
  }),

  timeToFill: (industryName = 'Technology') => ({
    title: 'Time to Fill (Days)',
    icon: '📅',
    description: 'Average number of days from job requisition approval to accepted offer. A key metric for hiring efficiency and business impact.',
    details: [
      'Includes all stages: sourcing, screening, interviewing, offer',
      'Directly impacts business operations and costs',
      'Affects quality of hire (best candidates go off market quickly)',
      'Major component is scheduling delays'
    ],
    benchmarkTable: {
      title: `${industryName} Time to Fill Benchmarks`,
      headers: ['Role Type', 'Days', 'Market Reality'],
      rows: [
        ['Entry Level', '25-35', 'High volume, quick decisions'],
        ['Professional', '35-50', 'Competitive market'],
        ['Technical/Specialized', '50-70', 'Scarce skills'],
        ['Leadership/Executive', '70-120', 'Extensive evaluation']
      ]
    },
    tips: [
      'Scheduling delays account for 30-40% of time to fill',
      'Every day of delay costs money (see Daily Vacancy Cost)',
      'Top performers fill positions 40% faster than average',
      'Focus on bottlenecks: usually scheduling and decision-making'
    ]
  }),

  candidateSatisfaction: (industryName = 'Technology') => ({
    title: 'Candidate Scheduling Experience',
    icon: '😊',
    description: 'How candidates rate their experience with your interview scheduling process on a scale of 1-10. This impacts your employer brand, candidate drop-off rates, and overall hiring success.',
    details: [
      'Measures ease of scheduling interviews',
      'Reflects communication clarity and timeliness',
      'Impacts candidate willingness to continue in process',
      'Affects employer brand and referrals',
      'Correlates with offer acceptance rates'
    ],
    benchmarkTable: {
      title: 'Industry Experience Benchmarks',
      headers: ['Score Range', 'Experience Level', 'Impact'],
      rows: [
        ['1-3', 'Poor', 'High drop-off, negative reviews'],
        ['4-5', 'Below Average', 'Some frustration, moderate drop-off'],
        ['6-7', 'Average', 'Acceptable but room for improvement'],
        ['8-9', 'Good', 'Positive experience, low drop-off'],
        ['10', 'Excellent', 'Exceptional, becomes competitive advantage']
      ]
    },
    tips: [
      'Survey recent candidates for accurate scores',
      'Consider entire scheduling journey from initial contact',
      'Factor in response times and flexibility offered',
      'Include feedback about rescheduling experiences',
      `Industry standard for ${industryName} is typically 6-7`
    ]
  }),

  interviewerSatisfaction: (industryName = 'Technology') => ({
    title: 'Interviewer Scheduling Experience',
    icon: '👔',
    description: 'How your interviewers and hiring managers rate the ease and efficiency of the scheduling process on a scale of 1-10. This affects interviewer participation and hiring velocity.',
    details: [
      'Measures time burden on interviewers',
      'Reflects scheduling tool effectiveness',
      'Impacts interviewer willingness to participate',
      'Affects overall hiring team productivity',
      'Indicates internal process efficiency'
    ],
    benchmarkTable: {
      title: 'Interviewer Satisfaction Levels',
      headers: ['Score Range', 'Satisfaction', 'Typical Issues'],
      rows: [
        ['1-3', 'Very Dissatisfied', 'Constant conflicts, manual coordination'],
        ['4-5', 'Dissatisfied', 'Frequent issues, time-consuming'],
        ['6-7', 'Neutral', 'Manageable but inefficient'],
        ['8-9', 'Satisfied', 'Smooth process, minimal friction'],
        ['10', 'Very Satisfied', 'Automated, seamless integration']
      ]
    },
    tips: [
      'Survey hiring managers and frequent interviewers',
      'Consider calendar integration quality',
      'Factor in last-minute change handling',
      'Include feedback about scheduling tool usability',
      `${industryName} companies typically score 5-7 without automation`
    ]
  }),

  interviewerAvailability: (industryName = 'Technology') => ({
    title: 'Interviewer Calendar Availability',
    icon: '📅',
    description: 'How easily schedulers can find available time slots for interviews on a scale of 1-10. Low scores indicate calendar congestion and scheduling bottlenecks.',
    details: [
      'Measures calendar openness and flexibility',
      'Indicates scheduling complexity',
      'Affects time-to-hire metrics',
      'Reflects interviewer workload balance',
      'Impacts candidate experience with wait times'
    ],
    benchmarkTable: {
      title: 'Availability Levels',
      headers: ['Score Range', 'Availability', 'Typical Wait Time'],
      rows: [
        ['1-2', 'Very Limited', '2+ weeks to find slots'],
        ['3-4', 'Limited', '1-2 weeks wait'],
        ['5-6', 'Moderate', '5-7 days wait'],
        ['7-8', 'Good', '3-4 days wait'],
        ['9-10', 'Excellent', 'Same week availability']
      ]
    },
    tips: [
      'Track average time to find suitable slots',
      'Consider dedicated interviewer time blocks',
      'Factor in multi-interviewer coordination complexity',
      'Monitor peak hiring season constraints',
      `${industryName} standard is typically 5-6 due to busy schedules`
    ]
  }),

  costPerHire: (industryName = 'Technology') => ({
    title: 'Cost Per Hire',
    icon: '💵',
    description: 'Total recruiting expenses divided by number of hires. Includes both external costs (job boards, agencies) and internal costs (recruiter time, overhead).',
    details: [
      'External costs: Job postings, agencies, background checks',
      'Internal costs: Recruiter salaries, interview time',
      'Technology and tools costs',
      'Onboarding and training expenses'
    ],
    benchmarkTable: {
      title: 'Cost Components Breakdown',
      headers: ['Component', 'Typical Range', '% of Total'],
      rows: [
        ['Advertising/Sourcing', '$500-2000', '10-20%'],
        ['Recruiter Time', '$1500-3000', '30-40%'],
        ['Interview Time', '$1000-2500', '20-30%'],
        ['Tools/Technology', '$300-800', '5-10%'],
        ['Admin/Coordination', '$500-1500', '10-20%']
      ]
    },
    tips: [
      'Include fully-loaded costs (salary + benefits + overhead)',
      'Track both successful hires and failed attempts',
      'Efficient scheduling can reduce cost per hire by 15-25%',
      'Consider opportunity cost of extended vacancies'
    ]
  })
};